import {
  isSncrRetencaoTipo,
  SNCR_BASIC,
  SNCR_NOTIFICACAO_QTD,
  SNCR_RETENCAO_QTD,
} from "../constants";
import type {
  SncrNotificacaoTipo,
  SncrNumeracaoBlocoStatus,
} from "../types";

const EXT = SNCR_BASIC.extensions;

export type SncrNumeracaoLote = {
  inicio: string;
  fim: string;
  quantidade: number;
  numeros: string[];
  created: string;
  mensagemApi?: string;
};

export type SncrNumeracaoBloco = {
  id: string;
  tipoReceita: SncrNotificacaoTipo;
  inicio: string;
  fim: string;
  quantidade: number;
  saldoReceitas?: number;
  status: SncrNumeracaoBlocoStatus;
  conselho: string;
  uf: string;
  documento: string;
  mensagemApi?: string;
  created?: string;
  numeros: string[];
  usados: string[];
  disponiveis: string[];
  lotes: SncrNumeracaoLote[];
  resource: fhir5.Basic;
};

function readString(extensions: fhir5.Extension[] | undefined, url: string) {
  return extensions?.find((item) => item.url === url)?.valueString;
}

function readCode(extensions: fhir5.Extension[] | undefined, url: string) {
  return extensions?.find((item) => item.url === url)?.valueCode;
}

function readInteger(extensions: fhir5.Extension[] | undefined, url: string) {
  return extensions?.find((item) => item.url === url)?.valueInteger;
}

function parseStringArray(raw?: string): string[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map(String).filter(Boolean);
    }
  } catch {
  }
  return [];
}

function parseLotesJson(raw?: string): SncrNumeracaoLote[] {
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item): SncrNumeracaoLote | null => {
        if (!item || typeof item !== "object") return null;
        const numeros = Array.isArray(item.numeros)
          ? item.numeros.map(String).filter(Boolean)
          : [];
        const inicio = String(item.inicio || numeros[0] || "");
        const fim = String(item.fim || numeros[numeros.length - 1] || "");
        const created = String(item.created || "");
        if (!inicio || !fim || !created) return null;
        return {
          inicio,
          fim,
          quantidade:
            typeof item.quantidade === "number"
              ? item.quantidade
              : numeros.length,
          numeros,
          created,
          ...(item.mensagemApi
            ? { mensagemApi: String(item.mensagemApi) }
            : {}),
        };
      })
      .filter((item): item is SncrNumeracaoLote => Boolean(item));
  } catch {
    return [];
  }
}

export function serializeLotes(lotes: SncrNumeracaoLote[]): string {
  return JSON.stringify(lotes);
}

export function uniqueConcatNumeros(
  existing: string[],
  incoming: string[],
): string[] {
  const seen = new Set(existing);
  const out = [...existing];
  for (const numero of incoming) {
    if (seen.has(numero)) continue;
    seen.add(numero);
    out.push(numero);
  }
  return out;
}

export function parseSncrNumeracaoBasic(
  resource: fhir5.Basic,
): SncrNumeracaoBloco | null {
  if (!resource.id) return null;
  if (
    !resource.code?.coding?.some(
      (coding) =>
        coding.system === SNCR_BASIC.codeSystem &&
        coding.code === SNCR_BASIC.code,
    )
  ) {
    return null;
  }

  const ext = resource.extension;
  const tipoReceita = readCode(ext, EXT.tipoReceita) as SncrNotificacaoTipo | undefined;
  const inicio = readString(ext, EXT.inicio);
  const fim = readString(ext, EXT.fim);
  if (!tipoReceita || !inicio || !fim) return null;

  const lotes = parseLotesJson(readString(ext, EXT.lotes));
  if (!lotes.length) return null;

  const numeros = parseStringArray(readString(ext, EXT.numeros));
  const usados = parseStringArray(readString(ext, EXT.numerosUsados));
  const usadosSet = new Set(usados);
  const disponiveis = numeros.filter((n) => !usadosSet.has(n));
  const statusCode = readCode(ext, EXT.status) as SncrNumeracaoBlocoStatus | undefined;

  return {
    id: resource.id,
    tipoReceita,
    inicio,
    fim,
    quantidade: readInteger(ext, EXT.quantidade) ?? numeros.length,
    saldoReceitas: readInteger(ext, EXT.saldoReceitas),
    status: statusCode ?? (disponiveis.length === 0 ? "esgotado" : "disponivel"),
    conselho: readString(ext, EXT.conselho) ?? "",
    uf: readString(ext, EXT.uf) ?? "",
    documento: readString(ext, EXT.documento) ?? "",
    mensagemApi: readString(ext, EXT.mensagemApi),
    created: resource.created,
    numeros,
    usados,
    disponiveis,
    lotes,
    resource,
  };
}

export function upsertExtension(
  extensions: fhir5.Extension[] | undefined,
  next: fhir5.Extension,
): fhir5.Extension[] {
  const list = [...(extensions ?? [])].filter((item) => item.url !== next.url);
  list.push(next);
  return list;
}

export function isSameLocalDay(iso?: string, now = new Date()): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export function isSameLocalMonth(iso?: string, now = new Date()): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth()
  );
}

function sumLoteQuantidade(
  blocos: SncrNumeracaoBloco[],
  tipo: SncrNotificacaoTipo,
  predicate: (created?: string) => boolean,
): number {
  return blocos
    .filter((bloco) => bloco.tipoReceita === tipo)
    .flatMap((bloco) => bloco.lotes)
    .filter((lote) => predicate(lote.created))
    .reduce((acc, lote) => acc + (lote.quantidade || lote.numeros.length), 0);
}

export function summarizeDailyQuota(
  blocos: SncrNumeracaoBloco[],
  tipo: SncrNotificacaoTipo,
  dailyLimit: number,
) {
  const solicitadosHoje = sumLoteQuantidade(blocos, tipo, isSameLocalDay);

  return {
    solicitadosHoje,
    restanteHoje: Math.max(0, dailyLimit - solicitadosHoje),
    dailyLimit,
  };
}

export function summarizeMonthlyQuota(
  blocos: SncrNumeracaoBloco[],
  tipo: SncrNotificacaoTipo,
  monthlyLimit: number,
) {
  const solicitadosMes = sumLoteQuantidade(blocos, tipo, isSameLocalMonth);

  return {
    solicitadosMes,
    restanteMes: Math.max(0, monthlyLimit - solicitadosMes),
    monthlyLimit,
  };
}

/** Cota canônica por tipo: notificação = dia/50; RCE/RET = mês/3000. */
export function summarizeSncrQuota(
  blocos: SncrNumeracaoBloco[],
  tipo: SncrNotificacaoTipo,
) {
  if (isSncrRetencaoTipo(tipo)) {
    const q = summarizeMonthlyQuota(
      blocos,
      tipo,
      SNCR_RETENCAO_QTD.monthlyLimit,
    );
    return {
      periodo: "mes" as const,
      restante: q.restanteMes,
      limite: q.monthlyLimit,
      solicitados: q.solicitadosMes,
      perRequest: SNCR_RETENCAO_QTD.perRequest,
    };
  }

  const q = summarizeDailyQuota(
    blocos,
    tipo,
    SNCR_NOTIFICACAO_QTD.dailyLimit,
  );
  return {
    periodo: "dia" as const,
    restante: q.restanteHoje,
    limite: q.dailyLimit,
    solicitados: q.solicitadosHoje,
    perRequest: SNCR_NOTIFICACAO_QTD.max,
  };
}
